import { Table, TableColumnsType } from "antd";
import _ from "lodash";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { useTranslation } from 'react-i18next';
import { getExamStatsByStudent } from "../../../api/statistics.api";
import useAxios from "../../../hooks/useAxios";
import config from "../../../lib/config";
import exportTitleTable from "../../../lib/exportTitleTable";
import { IExamStatsByStudent, IGetExamStatsByStudent } from "../../../models/statistics.model";
import DownloadButton from "../../DownloadButton";
import ErrorResponse from "../../ErrorResponse";
import Loader from "../../Loader";
import Pagination from "../../Pagination";

interface IProps {
  rawData: IExamStatsByStudent[]|undefined;
  total: number;
  pageNo: number;
  setPageNo: (pageNo: number) => void;
  examCode: number|undefined;
  semesterCode: number|undefined;
  campusCodes: string[];
  excelParam: {excelCampusCodes: string[]}|undefined;
}

const ExamStatsByStudent: FC<IProps> = ({
  rawData,
  total,
  pageNo,
  setPageNo,
  examCode,
  semesterCode,
  campusCodes,
  excelParam
}) => {
  const { t } = useTranslation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const { data: res, isLoading, error, execute } = useAxios<IGetExamStatsByStudent>();
  const [data, setData] = useState<IExamStatsByStudent[]>([]);

  useEffect(() => {
    if (rawData) {
      setData(handleData(rawData));
    }
  }, [rawData]);

  useEffect(() => {
    if (res?.data && !isLoading) {
      const currentDate = moment(new Date());

      let excelTitle = t('statistics.regularTest.3');
      let arrSubTitle = [];
      let campusNames = '';
  
      if(excelParam?.excelCampusCodes) {
        if(excelParam.excelCampusCodes.length === 0) {
          campusNames = t('common.text.all');
        } else {
          excelParam.excelCampusCodes.forEach((item) => {
            if(campusNames !== '') {
              campusNames += ', '  
            }
            campusNames += campusData.find(campus => item === campus.campusCode)?.campusKorName;
          });
        }
      }
  
      arrSubTitle.push(t('common.excel.campus') + " : " + campusNames);

      exportTitleTable(getColumns(), handleData(res.data), `${t('statistics.regularTest.3')}_(${currentDate.format('YYYYMMDD')})`, t('statistics.regularTest.4'), [12, 10, 20, 12, 20, 30, 10, 25, 12, 12, 12, 12, 12, 12, 10], excelTitle, arrSubTitle);
    }
  }, [res, isLoading]);

  const handleData = (rawData: any) => {
    let data: any = _.cloneDeep(rawData);
    if (rawData?.length) {
      data.forEach((item: any, idx: number) => {
        item.subjectInfo?.forEach((subitem: any) => {
          item[subitem.subjectName] = subitem.subjectScore;
        });
        item.rowIdx = idx;
      });
    }

    return data;
  }

  const getColumns = () => {
    const firstColumns: TableColumnsType<IExamStatsByStudent> = [
      {
        title: t('common.menu.campus'),
        dataIndex: 'campusKorName',
        key: 'campusKorName',
        align: 'center',
      },
      {
        title: t('student.student.column5'),
        dataIndex: 'memberKorName',
        key: 'memberKorName',
        align: 'center',
      },
      {
        title: t('student.student.column64'),
        dataIndex: 'memberCode',
        key: 'memberCode',
        align: 'center',
      },
      {
        title: t('statistics.dttt.column1'),
        dataIndex: 'courseName',
        key: 'courseName',
        align: 'center',
      },
      {
        title: t('common.menu.class'),
        dataIndex: 'className',
        key: 'className',
        align: 'center',
      },
      {
        title: t('class.class.column3'),
        dataIndex: 'examName',
        key: 'examName',
        align: 'center',
      },
      {
        title: t('student.student.column29'),
        dataIndex: 'teacherKorName',
        key: 'teacherKorName',
        align: 'center',
      },
      {
        title: t('statistics.placementTest.column7'),
        dataIndex: 'testDate',
        key: 'testDate',
        align: 'center',
      },
      {
        title: 'Overall',
        dataIndex: 'overall',
        key: 'overall',
        align: 'center',
        render: value => Math.round(value),
      },
    ];
    const lastColumns: TableColumnsType<IExamStatsByStudent> = [
      {
        title: t('statistics.placementTest.column1'),
        dataIndex: 'timeRequired',
        key: 'timeRequired',
        align: 'center',
        render: value => Math.round(value),
      },
      {
        title: t('statistics.regularTest.column2'),
        dataIndex: 'participantYn',
        key: 'participantYn',
        align: 'center',
      },
    ];
    const middleColumns: TableColumnsType<IExamStatsByStudent> = [];

    if (rawData?.length) {
      // @ts-ignore
      rawData.at(0).subjectInfo?.forEach(item => {
        middleColumns.push({
          title: item.subjectName,
          dataIndex: item.subjectName,
          key: item.subjectName,
          align: 'center',
          render: value => Math.round(value),          
        });
      });
    }

    return _.concat(firstColumns, middleColumns, lastColumns);
  }
  
  const onExportTable = () => {
    if (examCode && semesterCode) {
      execute(getExamStatsByStudent(examCode, {
        semesterCode,
        campusCode: auth.hqYn === 'Y'
          ? campusCodes
          : (campusCodes.length ? campusCodes : auth.campus),
        pageNo: 1,
        perPage: 9999999,
      }));  
    }
  }

  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />

      <div className="comm-table__area">
        <Table
          columns={getColumns()}
          dataSource={data}
          pagination={{
            current: pageNo,
            pageSize: config.pageSize,
          }}
          rowKey={'rowIdx'}
        />
      </div>
      <div className="comm-bx__between">
        <DownloadButton onClick={onExportTable} />
        <div className="common-paging center">
          <Pagination
            count={total}
            page={pageNo}
            setPage={setPageNo}
          />
        </div>
      </div>
    </>
  );
}

export default ExamStatsByStudent;
